/**
 * Environment utility functions for detecting the current environment
 * and determining environment-specific UI behavior.
 * 
 * This utility is aligned with the hostname-based environment detection
 * used in main.jsx for Sentry (staging vs. production) and extends it by
 * also treating localhost/127.0.0.1 as a dedicated "development" environment
 * to provide consistent, environment-aware behavior across the application.
 */

/**
 * Detects the current environment based on the hostname.
 * 
 * @returns {string} 'development', 'staging', or 'production'
 */
export const getEnvironment = () => {
    // __playwright_hostname is only ever set by Playwright's addInitScript during
    // tests.  It is never defined in production, so this has zero runtime cost.
    const Hostname = window.__playwright_hostname ?? window.location.hostname;
    
    // Check for staging environment
    if (Hostname.includes('staging')) {
        return 'staging';
    }
    
    // Check for local development
    if (Hostname.includes('localhost') || Hostname.includes('127.0.0.1')) {
        return 'development';
    }
    
    // Default to production
    return 'production';
};

/**
 * Determines if the environment badge/watermark should be shown.
 * 
 * @returns {boolean} true if the watermark should be displayed, false otherwise
 */
export const shouldShowEnvironmentWatermark = () => {
    const Environment = getEnvironment();
    // Only show watermark on staging environment (not on development or production)
    return Environment === 'staging';
};

/**
 * Gets the display label for the current environment.
 * 
 * @returns {string} Human-readable environment name
 */
export const getEnvironmentLabel = () => {
    const Environment = getEnvironment();
    
    switch (Environment) {
        case 'staging':
            return 'STAGING';
        case 'development':
            return 'DEVELOPMENT';
        case 'production':
        default:
            return 'PRODUCTION';
    }
};
